import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { COLUMN_DEFINITIONS, PAGE_SIZE_OPTIONS } from "./table.config";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import {
  fetchInvoices,
  selectAllInvoices,
  resetInvoices,
  invoiceUpdated,
} from "../../redux/reducers/invoices/invoicesSlice";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CreateModal from "./CreateModal";
import {
  useAddInvoiceMutation,
  useDeleteInvoicesMutation,
  useGetInvoicesQuery,
  useLazyGetInvoiceQuery,
  useUpdateInvoiceMutation,
} from "../../redux/services/invoices/invoicesApi";
import { usePrefetch } from "../../redux/api";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { createRoot } from "react-dom/client";
import InvoiceLayout from "../../layouts/InvoiceLayout";
import { useConfig } from "../../hooks";
import { getPdfConfig } from "./Show/table.configs/pdf";
import { useFetchAll } from "../../hooks/useFetchAll";

const PdfAutoDownloadTrigger = ({ download, handleDownloadPdf, onComplete }) => {
  useEffect(() => {
    if (!download || typeof handleDownloadPdf !== "function") return;

    let cancelled = false;
    (async () => {
      try {
        await handleDownloadPdf(false);
      } finally {
        if (!cancelled) onComplete?.();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [download, handleDownloadPdf, onComplete]);

  return null;
};

const Invoices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { appType } = useConfig();
  const [searchParams] = useSearchParams();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canCreateInvoices = hasPermission(PERMISSIONS.INVOICES_CREATE);
  const canEditInvoices = hasPermission(PERMISSIONS.INVOICES_EDIT);
  const canManageInvoices = hasAnyPermission([
    PERMISSIONS.INVOICES_CREATE,
    PERMISSIONS.INVOICES_EDIT,
    PERMISSIONS.INVOICES_DELETE,
  ]);

  const filterTypes = useMemo(
    () => [
      {
        id: "dueDate",
        type: "date",
        label: "Due Date",
        defaultText: "All Time",
        defaultValue: { id: "allTime", value: null },
      },
    ],
    []
  );

  const initialFilters = useMemo(() => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    if (!startDateParam || !endDateParam) return [];

    const start = new Date(startDateParam);
    const end = new Date(endDateParam);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

    const isSingle = start.toDateString() === end.toDateString();
    const def = filterTypes[0];
    return [
      {
        ...def,
        type: isSingle ? "singleDate" : "customRange",
        value: { start, end },
        displayText: isSingle
          ? start.toLocaleDateString()
          : `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      },
    ];
  }, [searchParams, filterTypes]);

  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { setFilters },
    queryResult: { data, isLoading },
  } = useFetchAll({
    queryHook: useGetInvoicesQuery,
    defaultPageSize: 20,
    initialFilters: initialFilters.map((f) => ({ id: f.id, value: f.value })),
  });

  const [updateInvoice, { isLoading: isUpdating }] = useUpdateInvoiceMutation();

  const [deleteInvoices, { isLoading: isDeleting }] =
    useDeleteInvoicesMutation();

  const [triggerGetInvoice] = useLazyGetInvoiceQuery();

  const [createModalOpen, setCreateModalOpen] = useState(false);

  function handleEdit(itemId) {
    navigate(`/invoices/${itemId}`);
  }

  async function handleDelete(items) {
    const jobs = items.map((item) => item.jobs.map((i) => i._id)).flat();
    const res = await deleteInvoices({
      data: items.map((item) => item._id),
      jobs,
    });

    toast(res.data.message, {
      type: res.data.status,
      toastId: "delete-invoices",
    });

    return res.data;
  }

  async function handleUpdate(e, setLoading) {
    const updateData = {
      id: e.row.original._id,
      data: e.data,
    };

    const res = await updateInvoice(updateData).unwrap();

    toast(res.message, {
      type: res.status,
      toastId: `update-invoice-${res.status}`,
    });
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }

  const cannotCreateOrEdit = !canManageInvoices;

  const { pdfConfig = { COLUMN_DEFINITIONS: [], TOTAL_COLUMN_DEFINITIONS: [] } } =
    useMemo(() => getPdfConfig(appType) || {}, [appType]);
  const pdfColumnDefs = pdfConfig?.COLUMN_DEFINITIONS || [];
  const pdfTotalsColumnDefs = pdfConfig?.TOTAL_COLUMN_DEFINITIONS || [];

  const downloadInvoicePdf = useCallback(
    async ({ invoice }) => {
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      document.body.appendChild(container);

      const root = createRoot(container);

      return new Promise((resolve, reject) => {
        let finished = false;

        const cleanup = () => {
          try {
            root.unmount();
          } catch {
            // ignore
          }
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        };

        const handleComplete = () => {
          if (finished) return;
          finished = true;
          setTimeout(() => {
            cleanup();
            resolve();
          }, 100);
        };

        try {
          root.render(
            <InvoiceLayout
              columns={pdfColumnDefs}
              totalColumns={pdfTotalsColumnDefs}
              invoice={invoice}
              appType={appType}
              download={true}
            >
              <PdfAutoDownloadTrigger
                download={true}
                onComplete={handleComplete}
              />
            </InvoiceLayout>
          );
        } catch (e) {
          finished = true;
          cleanup();
          reject(e);
        }

        setTimeout(() => {
          if (finished) return;
          finished = true;
          cleanup();
          reject(new Error("PDF generation timeout"));
        }, 15000);
      });
    },
    [appType, pdfColumnDefs, pdfTotalsColumnDefs]
  );

  const handleDownloadPDF = useCallback(
    async (selectedItems) => {
      const items = Array.isArray(selectedItems)
        ? selectedItems
        : selectedItems
        ? [selectedItems]
        : [];

      if (!items.length) {
        toast.info("Select at least one invoice to download.", {
          toastId: "invoices-download-pdf-none-selected",
        });
        return;
      }

      for (const item of items) {
        try {
          const id = item?._id;
          const invoice = id
            ? await triggerGetInvoice({ id, drivers: true }).unwrap()
            : item;
          await downloadInvoicePdf({ invoice });
        } catch (error) {
          console.error("Download failed:", error);
          toast.error("Failed to generate PDF", {
            toastId: "invoices-download-pdf-failed",
          });
        }
      }
    },
    [downloadInvoicePdf, triggerGetInvoice]
  );

  const columnsWithPdf = useMemo(() => {
    return [
      ...COLUMN_DEFINITIONS,
      {
        id: "download",
        header: "PDF",
        accessorKey: "download",
        cell: ({ row }) => (
          <button
            type="button"
            className="flex justify-center w-full cursor-pointer hover:text-primary-600"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadPDF(row.original);
            }}
            title="Download PDF"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </button>
        ),
      },
    ];
  }, [handleDownloadPDF]);

  return (
    <>
      <TableLayout
        id="invoices"
        useStyles
        loading={isLoading}
        columns={columnsWithPdf}
        data={data?.invoices}
        pagination={data}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleDownloadPDF={handleDownloadPDF}
        onUpdate={handleUpdate}
        fullTextSearch={handleGlobalSearch}
        filters={filterTypes}
        onFiltersChange={setFilters}
        initialFilters={initialFilters}
        mainAction={{ onClick: () => setCreateModalOpen(true) }}
        deleteLoading={isDeleting}
        hideActions={cannotCreateOrEdit}
        hideMainAction={cannotCreateOrEdit}
        hideCheckbox={cannotCreateOrEdit}
        meta={{
          updateData: handleUpdate,
        }}
      />
      <CreateModal
        modalOpen={createModalOpen}
        setModalOpen={setCreateModalOpen}
      />
    </>
  );
};

export default Invoices;
